import { Chart } from "chart.js/auto";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Api, SearchPerformanceStats } from "../api.js";
import { UserList } from "../components/admin/user-list.js";
import { BaseElement } from "../utils/ui-components.js";
export * from "../components/admin/admin-nav.js";
export * from "../components/admin/chat-sessions.js";
export * from "../components/admin/completion-events.js";
export * from "../components/admin/invitations.js";
export * from "../components/admin/search-events.js";
export * from "../components/admin/user-form.js";
export * from "../components/admin/user-list.js";
export * from "../components/admin/user-profile.js";

// Add this mixin to protect admin routes
const adminOnly = (baseClass: typeof BaseElement) => {
    return class extends baseClass {
        async firstUpdated(changedProperties: any) {
            try {
                const user = await Api.me();
                if (user?.role !== "admin") {
                    window.location.href = "/";
                    return;
                }
            } catch (e) {
                console.error("Failed to check if user is admin:", e);
                window.location.href = "/";
            } finally {
                await super.firstUpdated?.(changedProperties);
            }
        }
    };
};

@customElement("admin-dashboard")
export class AdminDashboard extends adminOnly(BaseElement) {
    render() {
        return html`
            <div class="min-h-screen bg-gray-100">
                <admin-nav></admin-nav>
                <div class="max-w-7xl mx-auto px-4 py-6">
                    <div class="mb-6">
                        <h1 class="text-2xl font-bold">Admin Dashboard</h1>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <a href="/admin/users" class="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow">
                            <h3 class="text-lg font-semibold mb-2">User Management</h3>
                            <p class="text-gray-600">Manage users and their roles</p>
                        </a>
                        <a href="/admin/searches" class="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow">
                            <h3 class="text-lg font-semibold mb-2">Search Analytics</h3>
                            <p class="text-gray-600">View search history and patterns</p>
                        </a>
                        <a href="/admin/completions" class="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow">
                            <h3 class="text-lg font-semibold mb-2">Completion Analytics</h3>
                            <p class="text-gray-600">Monitor AI completion usage</p>
                        </a>
                        <a href="/admin/chats" class="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow">
                            <h3 class="text-lg font-semibold mb-2">Chat Sessions</h3>
                            <p class="text-gray-600">View and manage user chat history</p>
                        </a>
                    </div>
                </div>
            </div>
        `;
    }
}

@customElement("admin-users")
export class AdminUsers extends adminOnly(BaseElement) {
    render() {
        return html`
            <div class="min-h-screen bg-gray-100">
                <admin-nav></admin-nav>
                <div class="max-w-7xl mx-auto px-4 py-6">
                    <div class="mb-6">
                        <h1 class="text-2xl font-bold">User Management</h1>
                    </div>
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div class="flex flex-col gap-6">
                            <user-form
                                @user-created=${(e: CustomEvent) => {
                                    const userList = this.renderRoot.querySelector("user-list") as UserList;
                                    if (userList) {
                                        userList.users = [e.detail, ...userList.users];
                                    }
                                }}
                            ></user-form>
                            <invitation-list></invitation-list>
                        </div>
                        <div class="lg:col-span-2">
                            <user-list></user-list>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}

@customElement("admin-searches")
export class AdminSearches extends adminOnly(BaseElement) {
    render() {
        return html`
            <div class="min-h-screen bg-gray-100">
                <admin-nav></admin-nav>
                <div class="max-w-7xl mx-auto px-4 py-6">
                    <div class="mb-6">
                        <h1 class="text-2xl font-bold">Search Analytics</h1>
                    </div>
                    <div class="space-y-6">
                        <search-performance-chart></search-performance-chart>
                        <search-events></search-events>
                    </div>
                </div>
            </div>
        `;
    }
}

@customElement("admin-completions")
export class AdminCompletions extends adminOnly(BaseElement) {
    render() {
        return html`
            <div class="min-h-screen bg-gray-100">
                <admin-nav></admin-nav>
                <div class="max-w-7xl mx-auto px-4 py-6">
                    <div class="mb-6">
                        <h1 class="text-2xl font-bold">Completion Analytics</h1>
                    </div>
                    <div class="grid gap-6">
                        <token-usage-chart></token-usage-chart>
                        <completion-events></completion-events>
                    </div>
                </div>
            </div>
        `;
    }
}

@customElement("user-page")
export class UserPage extends adminOnly(BaseElement) {
    render() {
        return html`
            <div class="min-h-screen bg-gray-100">
                <admin-nav></admin-nav>
                <div class="max-w-7xl mx-auto px-4">
                    <user-profile></user-profile>
                </div>
            </div>
        `;
    }
}

@customElement("token-usage-chart")
export class TokenUsageChart extends BaseElement {
    @property({ type: String })
    userId?: string;

    @state()
    private dateFrom: Date;

    @state()
    private dateTo: Date;

    @state()
    private stats: { date: string; input_tokens: number; output_tokens: number }[] = [];

    constructor() {
        super();
        this.dateTo = new Date();
        this.dateFrom = new Date();
        this.dateFrom.setDate(this.dateFrom.getDate() - 30);
    }

    async firstUpdated() {
        const fromInput = this.renderRoot.querySelector<HTMLInputElement>("#dateFrom");
        const toInput = this.renderRoot.querySelector<HTMLInputElement>("#dateTo");

        if (fromInput && toInput) {
            fromInput.value = this.dateFrom.toISOString().split("T")[0];
            toInput.value = this.dateTo.toISOString().split("T")[0];
        }

        await this.loadStats();
    }

    private async loadStats() {
        try {
            this.stats = await Api.getCompletionStats({
                userId: this.userId,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
            });
            this.renderChart();
        } catch (e) {
            console.error("Failed to load stats:", e);
        }
    }

    private handleDateChange() {
        const fromInput = this.renderRoot.querySelector<HTMLInputElement>("#dateFrom");
        const toInput = this.renderRoot.querySelector<HTMLInputElement>("#dateTo");

        if (fromInput?.value && toInput?.value) {
            this.dateFrom = new Date(fromInput.value);
            this.dateTo = new Date(toInput.value);
            this.loadStats();
        }
    }

    private renderChart() {
        const ctx = this.renderRoot.querySelector<HTMLCanvasElement>("#statsChart");
        if (!ctx) return;

        new Chart(ctx, {
            type: "bar",
            data: {
                labels: this.stats.map((s) => s.date),
                datasets: [
                    {
                        label: "Input Tokens",
                        data: this.stats.map((s) => s.input_tokens),
                        backgroundColor: "rgb(59, 130, 246)",
                        borderColor: "rgb(59, 130, 246)",
                    },
                    {
                        label: "Output Tokens",
                        data: this.stats.map((s) => s.output_tokens),
                        backgroundColor: "rgb(147, 51, 234)",
                        borderColor: "rgb(147, 51, 234)",
                    },
                ],
            },
            options: {
                responsive: true,
                aspectRatio: 2.5,
                scales: {
                    x: {
                        stacked: false,
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: "Tokens",
                        },
                    },
                },
                plugins: {
                    legend: {
                        position: "top",
                    },
                },
            },
        });
    }

    render() {
        return html`
            <div class="space-y-6">
                <div class="bg-white rounded-lg shadow p-4">
                    <div class="mb-4 flex gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700">From</label>
                            <input
                                type="date"
                                id="dateFrom"
                                @change=${this.handleDateChange}
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700">To</label>
                            <input
                                type="date"
                                id="dateTo"
                                @change=${this.handleDateChange}
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>
                    </div>
                    <canvas id="statsChart"></canvas>
                </div>
            </div>
        `;
    }
}

@customElement("admin-chat-sessions")
export class AdminChatSessions extends adminOnly(BaseElement) {
    render() {
        return html`
            <div class="min-h-screen bg-gray-100">
                <admin-nav></admin-nav>
                <div class="max-w-7xl mx-auto px-4 py-6">
                    <div class="mb-6">
                        <h1 class="text-2xl font-bold">Chat Sessions</h1>
                    </div>
                    <chat-sessions></chat-sessions>
                </div>
            </div>
        `;
    }
}

@customElement("admin-sql")
export class AdminSQL extends adminOnly(BaseElement) {
    @state()
    private result: any[] = [];

    @state()
    private error: string = "";

    @state()
    private loading: boolean = false;

    private async executeQuery(e: Event) {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const query = (form.querySelector("#sql-query") as HTMLTextAreaElement).value;

        this.loading = true;
        this.error = "";
        this.result = [];

        try {
            const result = await Api.executeAdminSQL(query);
            this.result = result;
        } catch (err) {
            this.error = err instanceof Error ? err.message : "Unknown error occurred";
        } finally {
            this.loading = false;
        }
    }

    render() {
        return html`
            <div class="min-h-screen bg-gray-100">
                <admin-nav></admin-nav>
                <div class="max-w-7xl mx-auto px-4 py-6">
                    <div class="mb-6">
                        <h1 class="text-2xl font-bold">SQL Console</h1>
                    </div>

                    <div class="bg-white rounded-lg shadow p-6">
                        <form @submit=${this.executeQuery} class="space-y-4">
                            <div>
                                <label for="sql-query" class="block text-sm font-medium text-gray-700">SQL Query</label>
                                <textarea
                                    id="sql-query"
                                    rows="4"
                                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="SELECT * FROM users LIMIT 10;"
                                ></textarea>
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    ?disabled=${this.loading}
                                >
                                    ${this.loading ? "Executing..." : "Execute Query"}
                                </button>
                            </div>
                        </form>

                        ${this.error ? html`<div class="mt-4 p-4 bg-red-50 text-red-700 rounded-md">${this.error}</div>` : nothing}
                        ${this.result.length > 0
                            ? html`
                                  <div class="mt-6 overflow-x-auto">
                                      <table class="min-w-full divide-y divide-gray-200">
                                          <thead class="bg-gray-50">
                                              <tr>
                                                  ${Object.keys(this.result[0]).map(
                                                      (key) => html`
                                                          <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                              ${key}
                                                          </th>
                                                      `
                                                  )}
                                              </tr>
                                          </thead>
                                          <tbody class="bg-white divide-y divide-gray-200">
                                              ${this.result.map(
                                                  (row) => html`
                                                      <tr>
                                                          ${Object.values(row).map(
                                                              (value) => html`
                                                                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${value}</td>
                                                              `
                                                          )}
                                                      </tr>
                                                  `
                                              )}
                                          </tbody>
                                      </table>
                                  </div>
                              `
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }
}

@customElement("search-performance-chart")
export class SearchPerformanceChart extends BaseElement {
    @state()
    private stats?: SearchPerformanceStats;

    private chart?: Chart;

    async firstUpdated() {
        await this.loadStats();
    }

    private async loadStats() {
        try {
            this.stats = await Api.getSearchPerformance(100); // Last 100 searches
        } catch (e) {
            console.error("Failed to load search performance:", e);
        }
    }

    updated() {
        this.renderChart();
    }

    private renderChart() {
        const ctx = this.renderRoot.querySelector<HTMLCanvasElement>("#statsChart");
        if (!ctx || !this.stats) return;

        // Destroy existing chart if it exists
        if (this.chart) {
            this.chart.destroy();
        }

        this.chart = new Chart(ctx, {
            type: "line",
            data: {
                labels: this.stats.timings.map((_, i) => `Search ${i + 1}`),
                datasets: [
                    {
                        label: "Total Time",
                        data: this.stats.timings.map((t) => t.total),
                        borderColor: "rgb(59, 130, 246)",
                        tension: 0.1,
                    },
                    {
                        label: "Search Time",
                        data: this.stats.timings.map((t) => t.search),
                        borderColor: "rgb(147, 51, 234)",
                        tension: 0.1,
                    },
                    {
                        label: "Serialize Time",
                        data: this.stats.timings.map((t) => t.serialize),
                        borderColor: "rgb(234, 179, 8)",
                        tension: 0.1,
                    },
                ],
            },
            options: {
                responsive: true,
                aspectRatio: 2.5,
                scales: {
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: "Time (ms)",
                        },
                    },
                },
                plugins: {
                    legend: {
                        position: "top",
                    },
                },
            },
        });
    }

    render() {
        if (!this.stats) {
            return html`<div class="text-center p-4">Loading...</div>`;
        }

        const avg = this.stats.summary.averages;

        return html`
            <div class="bg-white rounded-lg shadow p-6 space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="bg-blue-50 p-4 rounded-lg">
                        <h3 class="text-lg font-semibold text-blue-700">Average Total Time</h3>
                        <p class="text-2xl font-bold text-blue-900">${avg.total.toFixed(2)}ms</p>
                    </div>
                    <div class="bg-purple-50 p-4 rounded-lg">
                        <h3 class="text-lg font-semibold text-purple-700">Average Search Time</h3>
                        <p class="text-2xl font-bold text-purple-900">${avg.search.toFixed(2)}ms</p>
                    </div>
                    <div class="bg-yellow-50 p-4 rounded-lg">
                        <h3 class="text-lg font-semibold text-yellow-700">Average Serialize Time</h3>
                        <p class="text-2xl font-bold text-yellow-900">${avg.serialize.toFixed(2)}ms</p>
                    </div>
                </div>
                <div>
                    <canvas id="statsChart"></canvas>
                </div>
            </div>
        `;
    }
}
